// src/components/inventory/suppliers/SupplierForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Check, AlertCircle, Building, Mail, Phone, MapPin, User,
  Plus, X
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

// Default supplier categories
const DEFAULT_SUPPLIER_CATEGORIES = [
  'Raw Materials',
  'Packaging',
  'Electronics',
  'Office Supplies',
  'Manufacturing',
  'Transportation',
  'Services',
  'Software',
  'Other'
];

const SupplierForm = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const { currentUser, isDemoMode } = useAuth();
  const { activeBusiness } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [supplierCategories, setSupplierCategories] = useState(DEFAULT_SUPPLIER_CATEGORIES);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    notes: '',
    paymentTerms: '',
    taxIdentifier: '',
    customFields: []
  });

  // Fetch supplier data and custom categories
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !activeBusiness) return;
      
      setIsLoading(true);
      try {
        // Fetch custom categories for this business
        const categoriesQuery = query(
          collection(db, 'businessSettings'),
          where('businessId', '==', activeBusiness.id),
          where('settingType', '==', 'supplierCategories')
        );
        
        const categoriesSnap = await getDocs(categoriesQuery);
        if (!categoriesSnap.empty) {
          const categoriesDoc = categoriesSnap.docs[0];
          const categories = categoriesDoc.data().categories || [];
          
          // Combine default categories with custom ones, removing duplicates
          const uniqueCategories = Array.from(new Set([...DEFAULT_SUPPLIER_CATEGORIES, ...categories]));
          setSupplierCategories(uniqueCategories);
        }
        
        // If editing, fetch supplier data
        if (isEditing && supplierId) {
          const supplierDoc = await getDoc(doc(db, 'suppliers', supplierId));
          
          if (supplierDoc.exists()) {
            const data = supplierDoc.data();
            // Only load if this supplier belongs to the active business
            if (data.businessId === activeBusiness.id) {
              setFormData(data);
              
              // If the supplier has a category that's not in our list, add it
              if (data.category && !supplierCategories.includes(data.category)) {
                setSupplierCategories(prev => [...prev, data.category]);
              }
            } else {
              // If supplier doesn't belong to active business, redirect
              navigate('/inventory/suppliers');
            }
          } else {
            navigate('/inventory/suppliers');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isEditing, supplierId, currentUser, activeBusiness, navigate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    // Add to local state first
    const updatedCategories = [...supplierCategories, newCategory.trim()];
    setSupplierCategories(updatedCategories);
    
    // Set as current selection
    setFormData(prev => ({
      ...prev,
      category: newCategory.trim()
    }));
    
    // Reset add category UI
    setNewCategory('');
    setShowAddCategory(false);
    
    if (!isDemoMode) {
      try {
        // Save to Firestore
        const categoriesQuery = query(
          collection(db, 'businessSettings'),
          where('businessId', '==', activeBusiness.id),
          where('settingType', '==', 'supplierCategories')
        );
        
        const categoriesSnap = await getDocs(categoriesQuery);
        
        if (categoriesSnap.empty) {
          // Create new settings document
          await setDoc(doc(collection(db, 'businessSettings')), {
            businessId: activeBusiness.id,
            settingType: 'supplierCategories',
            categories: updatedCategories,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: currentUser.uid
          });
        } else {
          // Update existing settings document
          const settingsDoc = categoriesSnap.docs[0];
          await updateDoc(doc(db, 'businessSettings', settingsDoc.id), {
            categories: updatedCategories,
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error saving category:', error);
        // Still keep the category in local state even if saving to Firestore fails
      }
    }
  };

  // Add custom field
  const handleAddCustomField = () => {
    setFormData(prev => ({
      ...prev,
      customFields: [...prev.customFields, { key: '', value: '' }]
    }));
  };

  // Update custom field
  const handleCustomFieldChange = (index, field, value) => {
    const updatedFields = [...formData.customFields];
    updatedFields[index][field] = value;
    
    setFormData(prev => ({
      ...prev,
      customFields: updatedFields
    }));
  };

  // Remove custom field
  const handleRemoveCustomField = (index) => {
    const updatedFields = [...formData.customFields];
    updatedFields.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      customFields: updatedFields
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Supplier name is required';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isDemoMode) {
      alert("Demo Mode Restriction: In demo mode, data modifications are not saved. Sign up for a free account to use all features.");
      return;
    }

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      
      // Scroll to first error
      const firstErrorElement = document.querySelector('.text-red-600');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      return;
    }

    setIsLoading(true);

    try {
      // Prepare supplier data
      const supplierData = {
        ...formData,
        businessId: activeBusiness.id,
        updatedAt: serverTimestamp()
      };
      
      if (isEditing) {
        // Update existing supplier
        await updateDoc(doc(db, 'suppliers', supplierId), supplierData);
      } else {
        // Create a new supplier with a generated ID
        const newSupplierId = uuidv4();
        supplierData.createdAt = serverTimestamp();
        supplierData.createdBy = currentUser.uid;
        
        await setDoc(doc(db, 'suppliers', newSupplierId), supplierData);
      }
      
      // Navigate back to supplier list
      navigate('/inventory/suppliers');
      
    } catch (error) {
      console.error('Error saving supplier:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: 'An error occurred. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/inventory/suppliers')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Suppliers
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">
          {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
        </h1>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6">
        {/* General Error Message */}
        {formErrors.submit && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700 flex">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{formErrors.submit}</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Company Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border ${
                  formErrors.name ? 'border-red-300' : 'border-gray-300'
                } pl-10 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                placeholder="Supplier company name"
                disabled={isLoading}
              />
            </div>
            {formErrors.name && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.name}
              </p>
            )}
          </div>
          
          {/* Category with Add Feature */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            {!showAddCategory ? (
              <div className="flex">
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={`block w-full rounded-lg rounded-r-none border ${
                    formErrors.category ? 'border-red-300' : 'border-gray-300'
                  } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                  disabled={isLoading}
                >
                  <option value="">Select a category</option>
                  {supplierCategories.sort().map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddCategory(true)}
                  className="px-3 py-2.5 bg-gray-100 border border-gray-300 border-l-0 rounded-r-lg hover:bg-gray-200 text-gray-700"
                  disabled={isLoading}
                >
                  <Plus size={16} />
                </button>
              </div>
            ) : (
              <div className="flex">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="block w-full rounded-lg rounded-r-none border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  placeholder="Enter new category"
                  disabled={isLoading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="px-3 py-2.5 bg-[#190192] border border-[#190192] text-white rounded-none hover:bg-[#2C0DB5]"
                  disabled={isLoading || !newCategory.trim()}
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategory('');
                  }}
                  className="px-3 py-2.5 bg-gray-100 border border-gray-300 border-l-0 rounded-r-lg hover:bg-gray-200 text-gray-700"
                  disabled={isLoading}
                >
                  <X size={16} />
                </button>
              </div>
            )}
            {formErrors.category && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.category}
              </p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Contact Person */}
          <div>
            <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Person
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="contactPerson"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                className="block w-full rounded-lg border border-gray-300 pl-10 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Primary contact name"
                disabled={isLoading}
              />
            </div>
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={16} className="text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`block w-full rounded-lg border ${
                  formErrors.email ? 'border-red-300' : 'border-gray-300'
                } pl-10 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                placeholder="contact@supplier.com"
                disabled={isLoading}
              />
            </div>
            {formErrors.email && (
              <p className="mt-1 text-red-600 text-xs flex items-center">
                <AlertCircle size={12} className="mr-1" /> {formErrors.email}
              </p>
            )}
          </div>
        </div>
        
        {/* Rest of the form remains the same */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="block w-full rounded-lg border border-gray-300 pl-10 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="+1 (555) 123-4567"
                disabled={isLoading}
              />
            </div>
          </div>
          
          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="www.supplier.com"
              disabled={isLoading}
            />
          </div>
        </div>
        
        {/* Address */}
        <div className="mb-6">
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="block w-full rounded-lg border border-gray-300 pl-10 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="123 Business St, City, Country"
              disabled={isLoading}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Payment Terms */}
          <div>
            <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Terms
            </label>
            <input
              type="text"
              id="paymentTerms"
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleInputChange}
              className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="e.g., Net 30"
              disabled={isLoading}
            />
          </div>
          
          {/* Tax Identifier */}
          <div>
            <label htmlFor="taxIdentifier" className="block text-sm font-medium text-gray-700 mb-1">
              Tax ID / VAT Number
            </label>
            <input
              type="text"
              id="taxIdentifier"
              name="taxIdentifier"
              value={formData.taxIdentifier}
              onChange={handleInputChange}
              className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Tax identification number"
              disabled={isLoading}
            />
          </div>
        </div>
        
        {/* Notes */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows="3"
            value={formData.notes}
            onChange={handleInputChange}
            className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Additional notes about this supplier"
            disabled={isLoading}
          />
        </div>
        
        {/* Custom Fields Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-gray-700">Custom Fields</h3>
            <button
              type="button"
              onClick={handleAddCustomField}
              className="text-[#190192] hover:text-[#2C0DB5] text-sm font-medium"
              disabled={isLoading}
            >
              + Add Field
            </button>
          </div>
          
          {formData.customFields && formData.customFields.length > 0 ? (
            <div className="space-y-3">
              {formData.customFields.map((field, index) => (
                <div key={index} className="flex space-x-3">
                  <input
                    type="text"
                    value={field.key}
                    onChange={(e) => handleCustomFieldChange(index, 'key', e.target.value)}
                    className="block w-1/3 rounded-lg border border-gray-300 px-4 py-2 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Field name"
                    disabled={isLoading}
                  />
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                    className="block flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Field value"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomField(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                    disabled={isLoading}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No custom fields added yet. Click "Add Field" to create custom supplier fields.
            </p>
          )}
        </div>
        
        {/* Form Action Buttons */}
        <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={() => navigate('/inventory/suppliers')}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-6 py-2.5 bg-[#190192] text-white font-medium rounded-lg ${
              isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#2C0DB5]'
            } flex items-center`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Check size={18} className="mr-2" />
                {isEditing ? 'Update Supplier' : 'Save Supplier'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierForm;